import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { totpService } from '../../api/services';

const TwoFactorAuth: React.FC = () => {
  const [setupData, setSetupData] = useState<{ secret: string; otpauthUrl: string; qrDataUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const d = await totpService.setup();
      setSetupData(d);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur lors du setup 2FA');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error('Entrez le code à 6 chiffres'); return; }
    setBusy(true);
    try {
      await totpService.verify(code);
      setEnabled(true);
      toast.success('2FA activée');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Code invalide');
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error('Entrez votre code 2FA courant'); return; }
    setBusy(true);
    try {
      await totpService.disable(code);
      setEnabled(false);
      setSetupData(null);
      setCode('');
      toast.success('2FA désactivée');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Code invalide');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl bg-white p-6 rounded-xl border border-gray-200 space-y-4">
      <h2 className="text-lg font-semibold">Authentification à deux facteurs (TOTP)</h2>
      <p className="text-sm text-gray-500">
        Compatible Google Authenticator, Authy, 1Password. Une fois activée, un code
        à 6 chiffres sera demandé à chaque connexion.
      </p>

      {!setupData && !enabled && (
        <button onClick={start} disabled={busy}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
          {busy ? 'Chargement…' : 'Activer le 2FA'}
        </button>
      )}

      {setupData && !enabled && (
        <div className="space-y-4">
          <div className="flex gap-4 items-start">
            <img src={setupData.qrDataUri} alt="QR TOTP" className="w-44 h-44 border border-gray-200 rounded-lg" />
            <div className="text-sm text-gray-700 space-y-2">
              <p>Scannez ce QR code avec votre app d'authentification.</p>
              <p className="text-xs break-all text-gray-500">
                Ou saisissez la clé&nbsp;: <code className="bg-gray-100 px-1 py-0.5 rounded">{setupData.secret}</code>
              </p>
              <a href={setupData.otpauthUrl} className="text-xs text-blue-600">Ouvrir dans l'app (otpauth://)</a>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                   placeholder="Code à 6 chiffres"
                   className="w-40 rounded-lg border border-gray-200 px-3 py-2 text-center tracking-widest font-mono" />
            <button onClick={verify} disabled={busy}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
              Vérifier et activer
            </button>
          </div>
        </div>
      )}

      {enabled && (
        <div className="space-y-3">
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
            ✓ 2FA activée sur ce compte.
          </div>
          <div className="flex gap-2 items-center">
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                   placeholder="Code courant"
                   className="w-40 rounded-lg border border-gray-200 px-3 py-2 text-center font-mono" />
            <button onClick={disable} disabled={busy}
                    className="px-4 py-2 border border-red-300 text-red-700 rounded-lg font-semibold hover:bg-red-50 disabled:opacity-50">
              Désactiver le 2FA
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorAuth;
